import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import BrandMark from "../components/site/BrandMark";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user !== false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      navigate("/admin", { replace: true });
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:flex bg-slate-950 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,#2563EB_0,transparent_45%),radial-gradient(circle_at_80%_80%,#DC2626_0,transparent_40%)]" />
        <div className="relative z-10 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-3">
            <BrandMark className="h-14 w-14 drop-shadow-[0_3px_10px_rgba(96,165,250,0.5)]" />
            <div className="leading-none">
              <div className="font-heading font-black text-xl tracking-tight uppercase">Pneupro</div>
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-400">
                Admin Portal
              </div>
            </div>
          </Link>

          <div>
            <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-blue-400">
              Operations Console
            </div>
            <h1 className="font-heading mt-3 text-4xl lg:text-5xl font-black tracking-tighter uppercase">
              Manage every service request, in one place.
            </h1>
            <p className="mt-5 text-slate-400 max-w-md">
              Track service requests, dispatch technicians, update job status. Designed for
              coordinators and supervisors at Pneupro Air Compressor Services Ltd.
            </p>
          </div>

          <Link
            to="/"
            data-testid="back-to-site-link"
            className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-slate-400 hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to public site
          </Link>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md" data-testid="admin-login-card">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-blue-600">
            Sign In
          </div>
          <h2 className="font-heading mt-2 text-4xl font-black tracking-tighter uppercase text-slate-900">
            Admin Login
          </h2>
          <p className="mt-3 text-sm text-slate-600">
            Authorised personnel only. All sessions are logged.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="al-email" className="text-[11px] font-bold uppercase tracking-widest text-slate-700">
                Email
              </Label>
              <Input
                id="al-email"
                data-testid="admin-login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-none"
              />
            </div>
            <div>
              <Label htmlFor="al-pass" className="text-[11px] font-bold uppercase tracking-widest text-slate-700">
                Password
              </Label>
              <Input
                id="al-pass"
                data-testid="admin-login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-none"
              />
            </div>

            {error && (
              <div data-testid="admin-login-error" className="border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting}
              className="w-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 font-bold tracking-wide uppercase px-8 py-4 inline-flex items-center justify-center gap-2 transition-all"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
